import { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Stack,
    Typography,
} from '@mui/material';
import {
    ListBase,
    Pagination,
    Title,
    useCanAccess,
    useListContext,
} from 'react-admin';

import {
    type Receipt,
    type ResponsibleUser,
    fetchResponsibleUsers,
} from './receiptApi';
import { ReceiptDeleteDialog } from './ReceiptDeleteDialog';
import { ReceiptPreviewDialog } from './ReceiptPreviewDialog';
import { allocationColor, statusColor, localToday } from './receiptHelpers';
import { formatDate } from '@/utils/date_utils';
import { DailyReceiptsFilters, DAILY_STICKY_KEYS } from './DailyReceiptsFilters';

export const DailyReceiptsList = () => {
    const today = localToday();
    return (
        <ListBase
            resource="receipts"
            storeKey="receipts.daily"
            disableAuthentication
            filterDefaultValues={{
                transaction_date_gte: today,
                transaction_date_lte: today,
            }}
            filter={{ deleted_at: null }}
            perPage={25}
            sort={{ field: 'id', order: 'DESC' }}
            debounce={400}
        >
            <DailyReceiptsLayout />
        </ListBase>
    );
};

const DailyReceiptsLayout = () => {
    const { data, isPending, filterValues } = useListContext<Receipt>();
    const [users, setUsers] = useState<ResponsibleUser[]>([]);

    useEffect(() => {
        void fetchResponsibleUsers()
            .then(setUsers)
            .catch(() => undefined);
    }, []);

    // hasFilters: check if any non-sticky keys are present
    const hasNonStickyFilters = Object.keys(filterValues || {}).some(
        key => !DAILY_STICKY_KEYS.includes(key)
    );

    return (
        <Stack sx={{ width: '100%', gap: 1 }}>
            <Title title="Daily receipts" />
            <DailyReceiptsFilters users={users} />
            {!isPending && !data?.length && !hasNonStickyFilters ? (
                <Alert severity="info">No receipts in this date range.</Alert>
            ) : !isPending && !data?.length ? (
                <Alert severity="info">No receipts match these filters.</Alert>
            ) : (
                <>
                    <Card>
                        <DailyReceiptsContent users={users} />
                    </Card>
                    <Pagination rowsPerPageOptions={[10, 25, 50, 100]} />
                </>
            )}
        </Stack>
    );
};

const DailyReceiptsContent = ({ users }: { users: ResponsibleUser[] }) => {
    const { data: receipts = [], isPending, error } = useListContext<Receipt>();
    const { canAccess: canDelete } = useCanAccess({
        resource: 'receipt',
        action: 'delete',
    });
    const [preview, setPreview] = useState<Receipt | null>(null);
    const [deletingId, setDeletingId] = useState<number | null>(null);

    const userName = useMemo(() => {
        const byId = new Map(
            users.map(user => [user.id, user.name || user.email] as const)
        );
        return (id: number) => byId.get(id) || `User #${id}`;
    }, [users]);

    if (isPending) return null;
    if (error) {
        return <Alert severity="error">{error.message}</Alert>;
    }
    if (!receipts.length) {
        return null;
    }

    return (
        <>
            <Stack spacing={0}>
                {receipts.map(receipt => (
                    <CardContent
                        key={receipt.id}
                        sx={{
                            py: 1.5,
                            '&:last-child': { pb: 1.5 },
                            borderBottom: 1,
                            borderColor: 'divider',
                        }}
                    >
                        <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            justifyContent="space-between"
                            alignItems={{ sm: 'center' }}
                            spacing={1}
                        >
                            <Box>
                                <Typography>
                                    {receipt.merchant || receipt.original_filename}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {userName(receipt.responsible_user_id)}
                                    {' · '}
                                    Uploaded{' '}
                                    {formatDate(
                                        receipt.created_at,
                                        'MM-dd-yyyy HH:mm'
                                    )}
                                    {' · '}
                                    {receipt.transaction_date || 'No purchase date'}
                                    {' · '}
                                    {receipt.allocation_type === 'project_cost'
                                        ? 'Project cost (COGS)'
                                        : receipt.category}
                                    {receipt.project_no != null
                                        ? ` · Project #${receipt.project_no}`
                                        : ''}
                                </Typography>
                            </Box>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Typography>
                                    {receipt.amount ? `$${receipt.amount}` : 'No amount'}
                                </Typography>
                                <Chip
                                    size="small"
                                    variant="outlined"
                                    label={receipt.status.replaceAll('_', ' ')}
                                    color={statusColor(receipt.status)}
                                />
                                <Chip
                                    size="small"
                                    label={receipt.allocation_status.replaceAll(
                                        '_',
                                        ' '
                                    )}
                                    color={allocationColor(receipt.allocation_status)}
                                />
                                <Button
                                    size="small"
                                    onClick={() => setPreview(receipt)}
                                >
                                    View
                                </Button>
                                {canDelete && (
                                    <Button
                                        size="small"
                                        color="error"
                                        onClick={() => setDeletingId(receipt.id)}
                                    >
                                        Delete
                                    </Button>
                                )}
                            </Stack>
                        </Stack>
                    </CardContent>
                ))}
            </Stack>
            <ReceiptPreviewDialog
                receipt={preview}
                onClose={() => setPreview(null)}
            />
            <ReceiptDeleteDialog
                receiptId={deletingId}
                onClose={() => setDeletingId(null)}
            />
        </>
    );
};
